/**
 * Typed fetch wrapper.
 *
 * The API always answers `{ error, code }` on failure, so error handling is one place
 * rather than a try/catch in every screen. A 401 clears the session and bounces to sign in;
 * everything else surfaces the server's own wording, which the copy rules (11a) already
 * make suitable to show a user.
 */

import { record } from './breadcrumbs';

const TOKEN_KEY = 'schoolverse.token';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private browsing — the session simply does not persist */
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();

export function onUnauthorized(fn: Listener): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  /*
   * Every call in the app comes through here, so this is the one place a request has to be
   * noted for the issue-report trail — no patching of `fetch`, and nothing for a screen to
   * remember to do.
   */
  const startedAt = performance.now();
  const res = await fetch(path.startsWith('/api') ? path : `/api${path}`, {
    method,
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  record('api', `${method} ${path}`, `${res.status} in ${Math.round(performance.now() - startedAt)}ms`);

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : {};

  if (!res.ok) {
    const payload = data as { error?: string; code?: string; details?: unknown };
    if (res.status === 401) {
      setToken(null);
      for (const fn of unauthorizedListeners) fn();
    }
    throw new ApiError(res.status, payload.error ?? 'Something went wrong', payload.code ?? 'error', payload.details);
  }
  return data as T;
}

/**
 * Fetch a file the API renders rather than a JSON body.
 *
 * A plain link cannot be used for these: the session is a bearer token, not a cookie, so
 * the browser would send the request unauthenticated. Fetching to a blob and handing the
 * caller an object URL is the shortest thing that keeps the header on the request.
 */
async function blob(path: string): Promise<string> {
  const token = getToken();
  const res = await fetch(path.startsWith('/api') ? path : `/api${path}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  record('api', `GET ${path}`, `${res.status}`);
  if (!res.ok) {
    if (res.status === 401) {
      setToken(null);
      for (const fn of unauthorizedListeners) fn();
    }
    let message = 'Something went wrong';
    try {
      message = ((JSON.parse(await res.text()) as { error?: string }).error) ?? message;
    } catch {
      /* not a JSON error body — the status is all we have */
    }
    throw new ApiError(res.status, message, 'error');
  }
  return URL.createObjectURL(await res.blob());
}

const inflight = new Map<string, Promise<unknown>>();

export const api = {
  blob,
  get: <T>(path: string) => {
    /* One request per path in flight: React's StrictMode (and two panels asking at once) would otherwise fire it twice. */
    const key = `${getToken() ?? ''} ${path}`;
    const running = inflight.get(key);
    if (running) return running as Promise<T>;
    const p = request<T>('GET', path).finally(() => inflight.delete(key));
    inflight.set(key, p);
    return p;
  },
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  del: <T>(path: string) => request<T>('DELETE', path),
};

/**
 * Live-event subscription.
 *
 * SSE is the fast path. The `revision` on every frame is what makes it safe: if a frame is
 * missed — a dropped connection, or a client that reconnected to a different Cloud Run
 * instance — the next one it does see has a higher revision than its local state, and the
 * caller refetches. The poll is the floor under that, not the primary mechanism.
 */
export function subscribeToEvent(
  eventId: string,
  handlers: { onEvent?: (type: string, data: unknown) => void; onRevision: (revision: number) => void },
): () => void {
  const token = getToken();
  let source: EventSource | null = null;
  let poll: number | null = null;
  let closed = false;

  const handleFrame = (type: string, raw: string) => {
    try {
      const payload = JSON.parse(raw) as { revision?: number; data?: unknown };
      if (typeof payload.revision === 'number') handlers.onRevision(payload.revision);
      handlers.onEvent?.(type, payload.data);
    } catch {
      /* a malformed frame is not worth tearing the stream down for */
    }
  };

  const connect = () => {
    if (closed) return;
    source = new EventSource(`/api/events/${eventId}/stream?token=${encodeURIComponent(token ?? '')}`);
    const types = [
      'ping', 'participant_joined', 'participant_left', 'event_started', 'engagement_changed',
      'revealed', 'time_extended', 'response_received', 'groups_changed', 'station_advanced',
      'score_changed', 'event_paused', 'event_ended', 'results_released',
    ];
    for (const type of types) {
      source.addEventListener(type, (e) => handleFrame(type, (e as MessageEvent<string>).data));
    }
    source.onerror = () => {
      // EventSource retries on its own using the server's `retry:` hint; the poll below
      // covers the window where it cannot.
      source?.close();
      source = null;
      if (!closed) setTimeout(connect, 3000);
    };
  };

  connect();

  // Safety net for a split across instances: cheap, and only a revision check.
  poll = window.setInterval(async () => {
    if (closed) return;
    try {
      const state = await api.get<{ revision: number }>(`/events/${eventId}/state`);
      handlers.onRevision(state.revision);
    } catch {
      /* transient — the next tick tries again */
    }
  }, 8000);

  return () => {
    closed = true;
    source?.close();
    if (poll !== null) window.clearInterval(poll);
  };
}
